import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { redux } from 'Core/redux';
import { RobotsService } from 'Service/api/robots-service';
import { showToast } from 'Core/redux/toast/slice';

export const STORE_KEY = 'robots';
export const INITIAL_STATE = {
  data: [],
  loading: false,
  error: null,
  filters: {
    searchText: '',
  },
  selectedRobot: null,
  tileView: true,
  maintenanceRecords: [],
  selectedMaintenanceRecord: null,
  maintenanceData: null,
  cellHistoryData: [],
};

// Async thunks
export const fetchRobots = createAsyncThunk(
  'robots/fetchRobots',
  async (_, { rejectWithValue }) => {
    try {
      const response = await RobotsService.getAllRobots();
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load Robots');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load Robots');
    }
  },
);

export const addRobot = createAsyncThunk(
  'robots/addRobot',
  async (robotPayload, { rejectWithValue }) => {
    try {
      const response = await RobotsService.saveNewRobot(robotPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to add Robot');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add Robot');
    }
  },
);

// Alias for compatibility
export const addRobots = addRobot;

export const editRobots = createAsyncThunk(
  'robots/editRobots',
  async (robotPayload, { rejectWithValue }) => {
    try {
      const response = await RobotsService.saveNewRobot(robotPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to update Robot');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update Robot');
    }
  },
);

export const fetchRobotsWithFilters = () => (dispatch) => {
  dispatch(fetchRobots());
};

// Maintenance Records Thunks
export const fetchMaintenanceRecordsByRobotId = createAsyncThunk(
  'robots/fetchMaintenanceRecordsByRobotId',
  async (robotId, { rejectWithValue }) => {
    try {
      const response = await RobotsService.getMaintenanceRecordsByRobotId(robotId);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load maintenance records');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load maintenance records');
    }
  },
);

export const fetchMaintenanceRecordsDetails = createAsyncThunk(
  'robots/fetchMaintenanceRecordsDetails',
  async (recordId, { rejectWithValue }) => {
    try {
      const response = await RobotsService.getMaintenanceRecordDetails(recordId);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load maintenance record details');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load maintenance record details');
    }
  },
);

// Fetch robots by asset number for cell history
export const fetchRobotsByAssetNumber = createAsyncThunk(
  'robots/fetchByAssetNumber',
  async (assetNumber) => {
    const { response } = await RobotsService.getAllRobotsByAssetNumber(assetNumber);
    return response;
  }
);

export const addRobotMaintenanceRecord = createAsyncThunk(
  'robots/addRobotMaintenanceRecord',
  async (maintenanceData, { rejectWithValue }) => {
    try {
      const response = await RobotsService.addMaintenanceRecord(maintenanceData);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to add maintenance record');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add maintenance record');
    }
  },
);

export const { reducer, actions, caseReducers } = createSlice({
  name: 'robots',
  initialState: INITIAL_STATE,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    resetFilters: state => {
      state.filters = INITIAL_STATE.filters;
    },
    setRobot: (state, action) => {
      state.selectedRobot = action.payload;
    },
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    setTileView: (state, action) => {
      state.tileView = action.payload;
    },
    setMaintenanceData: (state, action) => {
      state.maintenanceData = action.payload;
    },
    clearMaintenanceData: (state) => {
      state.maintenanceData = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchRobots.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRobots.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchRobots.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addRobot.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addRobot.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const newItem = {
            ...action.payload,
            isNewlyCreated: true,
          };
          state.data = [newItem, ...state.data];
        }
      })
      .addCase(addRobot.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(editRobots.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editRobots.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.data.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editRobots.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Maintenance Records
      .addCase(fetchMaintenanceRecordsByRobotId.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceRecordsByRobotId.fulfilled, (state, action) => {
        state.loading = false;
        state.maintenanceRecords = action.payload;
      })
      .addCase(fetchMaintenanceRecordsByRobotId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchMaintenanceRecordsDetails.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceRecordsDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedMaintenanceRecord = action.payload;
      })
      .addCase(fetchMaintenanceRecordsDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addRobotMaintenanceRecord.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addRobotMaintenanceRecord.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(addRobotMaintenanceRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch robots by asset number (cell history)
      .addCase(fetchRobotsByAssetNumber.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRobotsByAssetNumber.fulfilled, (state, action) => {
        state.loading = false;
        state.cellHistoryData = action.payload;
      })
      .addCase(fetchRobotsByAssetNumber.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addRobot.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(fetchRobotsWithFilters());
    listenerApi.dispatch(
      showToast({
        message: 'Robot saved successfully',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addRobot.rejected, editRobots.rejected),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: action.payload,
        type: 'danger',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(editRobots.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: 'Robot updated successfully',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addRobotMaintenanceRecord.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: 'Maintenance record saved successfully',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addRobotMaintenanceRecord.rejected),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: action.payload || 'Failed to save maintenance record',
        type: 'danger',
      }),
    );
  },
});

redux.injectReducer(STORE_KEY, reducer);

// Export individual actions
export const { 
  setRobot, 
  setMaintenanceData, 
  clearMaintenanceData, 
  resetFilters, 
  setTileView 
} = actions;
